/**
 * Phase 128-UX-Metrics — 7-day analytics strip for /admin/whatsapp
 *
 * Reads the pre-aggregated metrics returned by GET /api/admin/whatsapp/metrics
 * and renders three hero KPIs + per-template sparkline bars. Privacy-safe:
 * counts only, never recipient numbers.
 */
import { TrendingUp, ShieldCheck, AlertTriangle } from 'lucide-react';

/** Tiny inline SVG bar-chart for a 7-day per-template trend. */
function Sparkline({ daily, accent = '#22c55e' }) {
  const data = Array.isArray(daily) ? daily : [];
  if (data.length === 0) return null;
  const max = Math.max(1, ...data);
  const barW = 100 / data.length;
  return (
    <svg
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      className="w-full h-6"
      aria-hidden
      data-testid="whatsapp-metrics-sparkline-svg"
    >
      {data.map((v, i) => {
        const h = (v / max) * 22 + 2; // min 2 so empty days still show a baseline tick
        return (
          <rect
            key={i}
            x={i * barW + 0.4}
            y={24 - h}
            width={barW - 0.8}
            height={h}
            fill={v > 0 ? accent : '#22314F'}
            opacity={v > 0 ? 0.9 : 0.45}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

function HeroCard({ label, value, sub, accent, Icon, testid }) {
  return (
    <div
      className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-2xl flex items-start gap-3"
      data-testid={testid}
      style={{ borderColor: accent + '40' }}
    >
      <div
        className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
      >
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#94A3B8]">{label}</div>
        <div className="text-xl sm:text-2xl font-black text-white mt-0.5 tabular-nums leading-tight">{value}</div>
        {sub && <div className="text-[11px] font-bold text-[#cbd5e1] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function fmtInt(n) {
  return new Intl.NumberFormat('en-IN').format(n || 0);
}

export default function WhatsAppMetricsStrip({ metrics, templates = [], days = 7, onDaysChange }) {
  if (!metrics) return null;

  const total = metrics.total || 0;
  const sent = metrics.by_status?.sent || 0;
  const failed = metrics.by_status?.failed || 0;
  const skipped = metrics.by_status?.skipped_no_recipient || 0;
  const successRate = metrics.success_rate || 0;
  const windowDays = metrics.window_days || days || 7;
  const byTemplate = metrics.by_template || {};

  // Build the per-template sparkline list — only include templates we know
  // about from the registry so we get human-readable labels.
  const templateRows = templates
    .map((t) => ({
      key: t.key,
      label: t.label,
      stats: byTemplate[t.key] || { total: 0, sent: 0, failed: 0, daily: Array(windowDays).fill(0) },
    }))
    // Sort by total dispatches DESC so the busiest templates land first.
    .sort((a, b) => (b.stats.total || 0) - (a.stats.total || 0));

  return (
    <div className="space-y-4" data-testid="admin-whatsapp-metrics-strip">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[11px] font-black tracking-[0.18em] uppercase text-[#22c55e]">
            Last {windowDays} days · Platform health
          </div>
          <h2 className="text-base font-black text-white mt-0.5">Delivery telemetry</h2>
        </div>
        <div className="flex items-center gap-2">
          {onDaysChange && (
            <select
              value={days}
              onChange={(e) => onDaysChange(parseInt(e.target.value, 10))}
              data-testid="admin-whatsapp-metrics-range"
              className="bg-[#07111F] border-2 border-[#22314F] text-white text-[11px] font-bold tracking-wider px-2.5 py-1.5 rounded-lg focus:border-[#22c55e] outline-none"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          )}
          {total === 0 && (
            <span
              className="text-[10px] font-black uppercase tracking-[0.12em] px-2 py-1 bg-slate-700 text-white rounded"
              data-testid="admin-whatsapp-metrics-empty"
            >
              No dispatches yet
            </span>
          )}
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid sm:grid-cols-3 gap-3">
        <HeroCard
          label={`${days}-day throughput`}
          value={fmtInt(total)}
          sub={total === 0 ? 'Awaiting first dispatch' : `${fmtInt(sent)} sent · ${fmtInt(failed)} failed`}
          accent="#22c55e"
          Icon={TrendingUp}
          testid="admin-whatsapp-metrics-total"
        />
        <HeroCard
          label="Delivery success rate"
          value={total === 0 ? '—' : `${successRate}%`}
          sub={
            total === 0
              ? 'Set Twilio creds to start'
              : skipped > 0
              ? `${fmtInt(skipped)} skipped (no phone)`
              : 'Across deliverable attempts'
          }
          accent={successRate >= 95 ? '#22c55e' : successRate >= 70 ? '#f59e0b' : '#ef4444'}
          Icon={ShieldCheck}
          testid="admin-whatsapp-metrics-success-rate"
        />
        <HeroCard
          label="Failures requiring review"
          value={fmtInt(failed)}
          sub={
            failed === 0
              ? 'Zero failures — pipeline green'
              : failed === total
              ? 'All failed · check Twilio creds'
              : `${((failed / Math.max(1, total)) * 100).toFixed(1)}% of attempts`
          }
          accent={failed === 0 ? '#22c55e' : '#ef4444'}
          Icon={AlertTriangle}
          testid="admin-whatsapp-metrics-failed"
        />
      </div>

      {/* Per-template sparklines — collapse if all-zero to keep the page clean */}
      {total > 0 && templateRows.length > 0 && (
        <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-2xl" data-testid="admin-whatsapp-metrics-templates">
          <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#94A3B8] mb-3">
            Per-template {days}-day pulse · sorted by volume
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {templateRows.map((row) => {
              const stats = row.stats;
              const tRate = stats.total > 0
                ? Math.round((stats.sent / Math.max(1, stats.total - (stats.skipped_no_recipient || 0))) * 100)
                : null;
              const accent = stats.total === 0
                ? '#22314F'
                : tRate >= 95 ? '#22c55e' : tRate >= 70 ? '#f59e0b' : '#ef4444';
              return (
                <div key={row.key} className="flex items-center gap-3" data-testid={`admin-whatsapp-metrics-template-${row.key}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-bold text-white truncate" title={row.label}>{row.label}</div>
                    <div className="text-[10px] font-bold text-[#94A3B8] font-mono mt-0.5 truncate">
                      {fmtInt(stats.total)} · {stats.total > 0 ? `${tRate ?? 0}% sent` : 'idle'}
                    </div>
                  </div>
                  <div className="w-24 sm:w-32 flex-shrink-0">
                    <Sparkline daily={stats.daily} accent={accent} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
